(function (global) {
  'use strict';

  const BUSINESS_TIME_ZONE = 'America/New_York';
  const OPEN_MINUTE = 9 * 60;
  const CLOSE_MINUTE = 17 * 60;
  const WEEKDAYS = new Set(['Mon', 'Tue', 'Wed', 'Thu', 'Fri']);

  const easternFormatter = new Intl.DateTimeFormat('en-US', {
    timeZone: BUSINESS_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23'
  });

  const dateLabelFormatter = new Intl.DateTimeFormat('en-US', {
    timeZone: 'UTC',
    weekday: 'long',
    month: 'long',
    day: 'numeric'
  });

  function pad(value) {
    return String(value).padStart(2, '0');
  }

  function dateKey(year, month, day) {
    return `${year}-${pad(month)}-${pad(day)}`;
  }

  function calendarParts(date) {
    const parts = {};

    easternFormatter.formatToParts(date).forEach((part) => {
      if (part.type !== 'literal') {
        parts[part.type] = part.value;
      }
    });

    return {
      year: Number(parts.year),
      month: Number(parts.month),
      day: Number(parts.day),
      weekday: parts.weekday,
      hour: Number(parts.hour),
      minute: Number(parts.minute)
    };
  }

  function addHoliday(map, year, monthIndex, day, name) {
    const holidayDate = new Date(Date.UTC(year, monthIndex, day));
    const actualKey = dateKey(
      holidayDate.getUTCFullYear(),
      holidayDate.getUTCMonth() + 1,
      holidayDate.getUTCDate()
    );
    map.set(actualKey, name);
    return holidayDate;
  }

  function addObservedFixedHoliday(map, year, monthIndex, day, name) {
    const holidayDate = addHoliday(map, year, monthIndex, day, name);
    const dayOfWeek = holidayDate.getUTCDay();

    if (dayOfWeek === 6) {
      const observed = new Date(holidayDate);
      observed.setUTCDate(observed.getUTCDate() - 1);
      map.set(
        dateKey(observed.getUTCFullYear(), observed.getUTCMonth() + 1, observed.getUTCDate()),
        `${name} (observed)`
      );
    } else if (dayOfWeek === 0) {
      const observed = new Date(holidayDate);
      observed.setUTCDate(observed.getUTCDate() + 1);
      map.set(
        dateKey(observed.getUTCFullYear(), observed.getUTCMonth() + 1, observed.getUTCDate()),
        `${name} (observed)`
      );
    }
  }

  function nthWeekdayOfMonth(year, monthIndex, weekday, occurrence) {
    const first = new Date(Date.UTC(year, monthIndex, 1));
    const offset = (weekday - first.getUTCDay() + 7) % 7;
    return 1 + offset + ((occurrence - 1) * 7);
  }

  function lastWeekdayOfMonth(year, monthIndex, weekday) {
    const last = new Date(Date.UTC(year, monthIndex + 1, 0));
    const offset = (last.getUTCDay() - weekday + 7) % 7;
    return last.getUTCDate() - offset;
  }

  function majorHolidayMap(startYear, endYear) {
    const holidays = new Map();

    for (let year = startYear; year <= endYear; year += 1) {
      addObservedFixedHoliday(holidays, year, 0, 1, "New Year's Day");
      addHoliday(
        holidays,
        year,
        4,
        lastWeekdayOfMonth(year, 4, 1),
        'Memorial Day'
      );
      addObservedFixedHoliday(holidays, year, 6, 4, 'Independence Day');
      addHoliday(
        holidays,
        year,
        8,
        nthWeekdayOfMonth(year, 8, 1, 1),
        'Labor Day'
      );
      addHoliday(
        holidays,
        year,
        10,
        nthWeekdayOfMonth(year, 10, 4, 4),
        'Thanksgiving'
      );
      addObservedFixedHoliday(holidays, year, 11, 25, 'Christmas');
    }

    return holidays;
  }

  function parseAdditionalClosures(value) {
    const closures = new Map();

    String(value || '')
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean)
      .forEach((line) => {
        const [rawDate, ...rawName] = line.split('|');
        const normalizedDate = rawDate.trim();

        if (/^\d{4}-\d{2}-\d{2}$/.test(normalizedDate)) {
          closures.set(normalizedDate, rawName.join('|').trim() || 'Special closure');
        }
      });

    return closures;
  }

  function closureMapFor(parts, additionalClosures) {
    const closures = majorHolidayMap(parts.year - 1, parts.year + 2);

    parseAdditionalClosures(additionalClosures).forEach((name, key) => {
      closures.set(key, name);
    });

    return closures;
  }

  function utcCalendarDate(year, month, day, dayOffset) {
    const date = new Date(Date.UTC(year, month - 1, day));
    date.setUTCDate(date.getUTCDate() + dayOffset);
    return date;
  }

  function businessDateDetails(date, closures) {
    const year = date.getUTCFullYear();
    const month = date.getUTCMonth() + 1;
    const day = date.getUTCDate();
    const key = dateKey(year, month, day);
    const weekday = date.getUTCDay();

    return {
      year,
      month,
      day,
      key,
      isWeekday: weekday >= 1 && weekday <= 5,
      closureName: closures.get(key) || ''
    };
  }

  function nextOpening(parts, closures, startOnNextDay) {
    for (let offset = startOnNextDay ? 1 : 0; offset <= 370; offset += 1) {
      const candidate = utcCalendarDate(parts.year, parts.month, parts.day, offset);
      const details = businessDateDetails(candidate, closures);

      if (details.isWeekday && !details.closureName) {
        return {
          ...details,
          label: dateLabelFormatter.format(candidate),
          hour: 9,
          minute: 0
        };
      }
    }

    return null;
  }

  function evaluateBusinessStatus(date, config) {
    const safeDate = date instanceof Date ? date : new Date(date);
    const safeConfig = config || {};
    const parts = calendarParts(safeDate);
    const todayKey = dateKey(parts.year, parts.month, parts.day);
    const closures = closureMapFor(parts, safeConfig.additionalClosures);
    const closureName = closures.get(todayKey) || '';
    const minuteOfDay = (parts.hour * 60) + parts.minute;
    const withinHours = minuteOfDay >= OPEN_MINUTE && minuteOfDay < CLOSE_MINUTE;
    const autoOpen = WEEKDAYS.has(parts.weekday) && !closureName && withinHours;
    const override = safeConfig.override === 'open' || safeConfig.override === 'closed'
      ? safeConfig.override
      : 'auto';
    const isOpen = override === 'open' || (override === 'auto' && autoOpen);
    const forcedClosed = override === 'closed';
    let nextOpen = null;

    if (!isOpen && !forcedClosed) {
      const canOpenToday = WEEKDAYS.has(parts.weekday) && !closureName && minuteOfDay < OPEN_MINUTE;
      nextOpen = nextOpening(parts, closures, !canOpenToday);
    }

    return {
      isOpen,
      override,
      closureName,
      forcedClosed,
      parts,
      nextOpen
    };
  }

  function panelCopy(status) {
    if (status.isOpen) {
      return {
        shortStatus: 'Open',
        title: "We're open now",
        schedule: status.override === 'open'
          ? "We're available now."
          : "We're here until 5:00 PM Eastern.",
        message: "Call or text Brian, email us, or leave a voicemail. If we miss you, we'll get back to you as soon as we can."
      };
    }

    const title = status.closureName
      ? `We're closed for ${status.closureName.replace(' (observed)', '')}`
      : "We're closed right now";
    const schedule = status.forcedClosed
      ? "We'll be back during our normal business hours."
      : status.nextOpen
        ? `We'll reopen ${status.nextOpen.label} at 9:00 AM Eastern.`
        : "We'll be back during our normal business hours.";

    return {
      shortStatus: 'Closed',
      title,
      schedule,
      message: "You can still text, email, or leave a voicemail—we'll get back to you as soon as we're back."
    };
  }

  function setPanelOpen(container, shouldOpen, returnFocus) {
    const panel = container.querySelector('[data-business-status-panel]');
    const toggle = container.querySelector('[data-business-status-toggle]');

    if (!panel || !toggle) return;

    container.classList.toggle('is-status-open', shouldOpen);
    panel.setAttribute('aria-hidden', shouldOpen ? 'false' : 'true');
    toggle.setAttribute('aria-expanded', shouldOpen ? 'true' : 'false');

    if (shouldOpen) {
      panel.removeAttribute('inert');
    } else {
      panel.setAttribute('inert', '');
    }

    if (!shouldOpen && returnFocus) {
      toggle.focus();
    }
  }

  function renderStatus(container, config, now) {
    const status = evaluateBusinessStatus(now || new Date(), config);
    const copy = panelCopy(status);

    container.dataset.businessState = status.isOpen ? 'open' : 'closed';

    const shortStatus = container.querySelector('[data-business-short-status]');
    const title = container.querySelector('[data-business-status-title]');
    const schedule = container.querySelector('[data-business-status-schedule]');
    const message = container.querySelector('[data-business-status-message]');

    if (shortStatus) shortStatus.textContent = copy.shortStatus;
    if (title) title.textContent = copy.title;
    if (schedule) schedule.textContent = copy.schedule;
    if (message) message.textContent = copy.message;

    return status;
  }

  function initializeContainer(container) {
    if (container.dataset.businessHoursInitialized === 'true') return;

    const configNode = container.querySelector('[data-business-hours-config]');
    let config = {};

    try {
      config = configNode ? JSON.parse(configNode.textContent) : {};
    } catch (error) {
      config = {};
    }

    container.dataset.businessHoursInitialized = 'true';
    renderStatus(container, config);

    const toggle = container.querySelector('[data-business-status-toggle]');
    const panel = container.querySelector('[data-business-status-panel]');
    let closeTimer;

    const openPanel = () => {
      global.clearTimeout(closeTimer);
      setPanelOpen(container, true, false);
    };
    const closePanel = (returnFocus) => {
      global.clearTimeout(closeTimer);
      setPanelOpen(container, false, returnFocus);
    };
    const scheduleClose = () => {
      global.clearTimeout(closeTimer);
      closeTimer = global.setTimeout(() => {
        if (!container.matches(':focus-within')) {
          closePanel(false);
        }
      }, 180);
    };

    container.addEventListener('mouseenter', openPanel);
    container.addEventListener('mouseleave', scheduleClose);
    container.addEventListener('focusin', openPanel);
    container.addEventListener('focusout', scheduleClose);

    if (panel) {
      panel.addEventListener('mouseenter', openPanel);
      panel.addEventListener('mouseleave', scheduleClose);
    }

    if (toggle) {
      toggle.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        setPanelOpen(container, !container.classList.contains('is-status-open'), false);
      });
    }

    container.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && container.classList.contains('is-status-open')) {
        event.preventDefault();
        closePanel(true);
      }
    });

    document.addEventListener('pointerdown', (event) => {
      if (!container.contains(event.target)) {
        closePanel(false);
      }
    });

    global.setInterval(() => renderStatus(container, config), 60 * 1000);
  }

  function initializeAll(root) {
    const scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll('[data-business-hours]').forEach(initializeContainer);
  }

  const api = {
    BUSINESS_TIME_ZONE,
    evaluateBusinessStatus,
    majorHolidayMap,
    panelCopy,
    parseAdditionalClosures,
    renderStatus
  };

  global.SSCBusinessHours = api;

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = api;
  }

  if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => initializeAll(document));
    } else {
      initializeAll(document);
    }

    document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
  }
})(typeof window !== 'undefined' ? window : globalThis);
